<script setup lang="ts">
import {inject, ref, watch, watchEffect} from 'vue';
import {LegacyAdjustmentCategory, LegacyOrder, legacyOrderKey} from '@hantera/portal-app';
import {DecimalInput, DecimalInputValue, DropdownButton, DropdownItem} from '@hantera/design-system';
import {AdjustmentType} from './index';

const props = defineProps<{
  selectedCategory?: LegacyAdjustmentCategory
  value: DecimalInputValue
  type?: AdjustmentType
}>();

const emit = defineEmits<{
  (e: 'update:selected-category', value: LegacyAdjustmentCategory): void
  (e: 'update:value', value: DecimalInputValue): void
  (e: 'submit'): void
  (e: 'cancel'): void
}>();

defineExpose({
  setValueError() {
    valueElem.value?.setError();
  }
});

const order = inject(legacyOrderKey, ref<LegacyOrder>(<any>{}));
const valueElem = ref<InstanceType<typeof DecimalInput>>();
const selectedCategory = ref<LegacyAdjustmentCategory>();
const innerValue = ref(props.value);

const minValue = props.type === AdjustmentType.Fee ? 0 : undefined;
const maxValue = props.type === AdjustmentType.Discount ? 0 : undefined;

watchEffect(() => {
  selectedCategory.value = props.selectedCategory;
});

function select(value: LegacyAdjustmentCategory) {
  selectedCategory.value = value;
  emit('update:selected-category', value);
}

watch(innerValue, () => {
  emit('update:value', innerValue.value);
});

function submit() {
  if (!selectedCategory.value) {
    // TODO Add error state
  }
  emit('submit');
}

</script>


<template>
  <div class="order-adjustment-form">
    <DropdownButton
      :label="selectedCategory?.name || 'Select category...'"
      class="order-adjustment-form__category-select"
    >
      <DropdownItem
        v-for="c in order.adjustmentCategories"
        @click="select(c)"
      >
        {{ c.name }}
      </DropdownItem>
    </DropdownButton>

    <div class="order-adjustment-form__value">
      <DecimalInput
        v-model="innerValue"
        ref="valueElem"
        :select-on-focus="true"
        :min-value="minValue"
        :max-value="maxValue"
        :allow-percentage="true"
        @keyenter="submit()"
      />
    </div>

    <div class="order-adjustment-form__buttons">
      <button
        class="order-adjustment-form__submit btn-primary"
        @click="submit()"
      >&check;</button>
      <button
        class="order-adjustment-form__cancel"
        @click="emit('cancel')"
      >&#x2717;</button>
    </div>
  </div>
</template>

<style>
.order-adjustment-form {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 100px;
  gap: 8px;
  width: 100%;
  grid-column: 1 / 3;
}

.order-adjustment-form__value {
  width: 100px;
}

.order-adjustment-form__value input {
  text-align: right;
  height: 22px;
}

.order-adjustment-form__category-select {
  height: 22px;
}

.order-adjustment-form__buttons {
  grid-column: 1 / 3;
  display: grid;
  gap: 8px;
  grid-template-columns: auto min-content min-content;
}

.order-adjustment-form__submit {
  grid-column: 2 / 3;
}

.order-adjustment-form__cancel {
  grid-column: 3 / 4;
}
</style>

